import { useState, useEffect, useCallback, useRef } from 'react';
import { stageLabel } from '../constants/designStages';

/**
 * The hardware inputs Aurora actually designs against.
 *
 * Only these force a new design. Batteries and EV chargers are priced locally
 * and are never sent to Aurora, so changing them cannot alter the panel layout
 * — regenerating for them costs the customer a minute and spends Aurora credits
 * for an identical result.
 */
export const designInputsOf = (hardware) =>
  JSON.stringify({
    module: hardware?.module?.auroraModuleName ?? null,
    inverter: hardware?.inverter?.auroraInverterName ?? null,
    inverterType: hardware?.inverter?.column1?.Type ?? null,
    offset: Number(hardware?.offset ?? 0).toFixed(1),
    targetEnergyAnnual: Number(hardware?.targetEnergyAnnual ?? 0).toFixed(3),
  });

/** Everything else the customer can change — priced, but not designed. */
export const pricedExtrasOf = (hardware) =>
  JSON.stringify({
    battery: hardware?.battery?.name ?? null,
    batteryQuantity: hardware?.batteryQuantity ?? 0,
    service: hardware?.services?.name ?? null,
    serviceQuantity: hardware?.serviceQuantity ?? 0,
  });

const useAutoDesigner = ({
    hardware,
    updateHardware,
    responseState,
    formState,
    setDesignLocked,
}) => {
    const [designStatus, setDesignStatus] = useState('pending');
    const [submitted, setSubmitted] = useState(false);
    const [designInfo, setDesignInfo] = useState(null);
    const [isPolling, setIsPolling] = useState(true);
    // Screenshots arrive after the design completes, so they are tracked
    // separately from designStatus. MAX_SCREENSHOT_WAITS * 3s bounds how long
    // we keep polling for them once the design itself is done.
    const [screenshotsReady, setScreenshotsReady] = useState(false);
    // `ready` = main design image present (hides the "rendering" badge).
    // `done`  = every angle collected, or the bot run ended (stops polling).
    const [screenshotsDone, setScreenshotsDone] = useState(false);
    const [screenshotWaits, setScreenshotWaits] = useState(0);
    const MAX_SCREENSHOT_WAITS = 40; // ~2 minutes
    // The design inputs the current design was built from, captured at submit.
    // Lets the Design page tell "you changed the panels" from "you added a
    // battery" and avoid regenerating for the latter.
    const [generatedInputs, setGeneratedInputs] = useState(null);
    const [generatedExtras, setGeneratedExtras] = useState(null);
    // Guards the post-design re-baseline below so it happens once per run.
    const rebaselinedRef = useRef(false);

    // Function to start the design automation process
    const startAutomation = async () => {
        setDesignLocked(true);

        // console.log("module", hardware.module.auroraModuleName);

        // Always the inverter the customer actually selected.
        //
        // This used to be rewritten to "Powerwall 3 (integrated inverter)" when
        // the customer paired a Powerwall 3 with the Tesla Inverter. That name
        // does exist in Aurora's catalog, but it is the inverter built INTO a
        // Powerwall 3 — and we send Aurora no battery component for it to
        // attach to, so AutoDesigner had nothing to place and failed with
        // "Could not produce a design" (6500 Tanglewood, 2026-08-10).
        //
        // Battery selection is a pricing input only; it is deliberately not
        // part of the Aurora design request.
        const inverterName = hardware.inverter.auroraInverterName;

        // Clear the previous run before starting a new one. This used to happen
        // when the popup was closed, which meant simply going back to tweak a
        // battery destroyed a finished design — the customer returned to a
        // loading screen that never moved, and the bottom bar vanished with
        // designLocked. It belongs here: the poll effect skips terminal
        // statuses, so a stale 'completed' would stop the new run being tracked.
        setDesignStatus('pending');
        setDesignInfo(null);
        setScreenshotsReady(false);
        setScreenshotsDone(false);
        setScreenshotWaits(0);
        updateHardware('designInfo', null);

        setSubmitted(true);
        setGeneratedInputs(designInputsOf(hardware));
        setGeneratedExtras(pricedExtrasOf(hardware));
        rebaselinedRef.current = false;

        const targetEnergyAnnual = hardware.targetEnergyAnnual * 1000;
        const targetEnergy = targetEnergyAnnual > 30000 ? 30000 : targetEnergyAnnual.toFixed(1);

        const body = {
            address: formState.address,
            customerName: formState.name,
            module: hardware.module.auroraModuleName,
            inverter: inverterName,
            inverterType: hardware.inverter.column1.Type,
            // No battery is sent: Aurora is asked for a solar design only.
            targetOffset: hardware.offset.toFixed(1),
            targetEnergy: targetEnergy,
            hardware: hardware
        };

        try {
        const response = await fetch(process.env.REACT_APP_API_URL + '/estimate/run-automation', {
            method: 'POST',
            headers: {
            'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });
        let result = {};
        try {
            result = await response.json();
        } catch (parseError) {
            result = {};
        }

        if (!response.ok) {
            // Any non-2xx means the design pipeline did not start. Only the
            // exact string 'AI Roof Failed' used to be detected, so
            // 'AutoDesigner Failed' and 'Inverter type not found' silently left
            // the UI polling a design that would never arrive.
            console.error('run-automation failed', response.status, result);
            setDesignStatus('failedFinal');
            return;
        }

        if(result.message === 'AI Roof Failed'){
            setDesignStatus('failedFinal');
            return;
        }

        setIsPolling(true);
        } catch (error) {
        console.error('Error:', error);
        // The request may still have reached the backend, so poll rather than
        // stranding the UI on 'pending' with no way forward.
        setIsPolling(true);
        }
    };

    // Function to create proposals
    const createProposals = useCallback(async () => {
        const { projectId } = responseState.projectId;
        const adders = [];

        // Map hardware items to the adder names and quantities
        if (hardware.battery && hardware.batteryQuantity > 0) {
            const adderName = hardware.battery.name === 'Tesla Powerwall 3'
                ? 'Tesla Powerwall 3 (Storage Only)'
                : hardware.battery.name === 'Enphase 5P'
                ? 'Enphase 5P (Solo)'
                : hardware.battery.name;
            adders.push({ name: adderName, quantity: hardware.batteryQuantity });
        }

        if (hardware.inverter) {
            // Only add inverters if it's not the Enphase 5P, since it's a battery.
            const adderName = hardware.inverter.name;
            adders.push({ name: adderName, quantity: 1 }); // Assuming 1 inverter per design
        }

        if (hardware.evCharger && hardware.evChargerQuantity > 0) {
            const adderName = hardware.evCharger.name === 'Wallbox Pulsar Plus 2'
                ? 'Wallbox EV Charger (Pulsar Plus Level 2 - 40A) - FL Only'
                : hardware.evCharger.name;
            adders.push({ name: adderName, quantity: hardware.evChargerQuantity });
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/estimate/createProposals`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ projectId, adders }),
            });
            const result = await response.json();
            // console.log('Proposal creation result:', result);
        } catch (error) {
            console.error('Error creating proposals:', error);
        }
    }, [hardware.battery, hardware.batteryQuantity, hardware.evCharger, hardware.evChargerQuantity, hardware.inverter, responseState.projectId]);

    // Function to update proposals
    const createDesign = async (projectId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/estimate/createDesign?projectId=${projectId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const result = await response.json();
            // console.log('Create Design result:', result);
        } catch (error) {
            console.error('Error creating design:', error);
        }
    };

    // Function to update proposals
    const updateProposals = async (projectId, downPayment) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/estimate/updateProposals`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ projectId, downPayment }),
            });
            const result = await response.json();
            // console.log('Proposal update result:', result);
        } catch (error) {
            console.error('Error updating proposals:', error);
        }
    };

    // Function to select a proposal
    const selectProposal = async (projectId, proposalId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/estimate/selectProposal`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ projectId, proposalId }),
            });
            const result = await response.json();
            // console.log('Proposal selection result:', result);
        } catch (error) {
            console.error('Error selecting proposal:', error);
        }
    };

    // Function to fetch the design status
    const fetchDesignStatus = useCallback(async () => {
        try {
            // The address MUST be encoded: it goes into the URL path, and any
            // unit number containing '#' (e.g. "411 Bargello Ave SE #10, ...")
            // would otherwise be truncated at the '#' as a URL fragment. The
            // server then looks up a partial address, never matches the
            // customer, and the page polls a finished design forever.
            const response = await fetch(`${process.env.REACT_APP_API_URL}/estimate/getCustomerDesignStatus/${encodeURIComponent(formState.address)}`);

            if (!response.ok) {
                // Keep polling — a 404 (address mismatch) or 5xx is not a
                // terminal design state, and throwing here would land in the
                // catch below and freeze the status on its previous value.
                console.error('Design status poll failed', response.status);
                return;
            }

            const result = await response.json();

            // Update design info and status
            updateHardware('designInfo', result);

            switch (result.designStatus) {
                case 'initial':
                    setDesignStatus('initial');
                    // Deliberately NOT `result.initial`: that screenshot was
                    // Aurora's empty CAD canvas (dark grid + location pin) taken
                    // before any imagery rendered, and showing it replaced the
                    // customer's house with a blank grid for the whole loading
                    // phase. Keep the satellite view until a real design render
                    // exists. (The bot no longer captures it at all.)
                    updateHardware('designImage', hardware.satelliteImage);
                    updateHardware('designText', "Determining Roof Dimensions...");
                    break;
                case 'roof':
                    setDesignStatus('roof');
                    updateHardware('designImage', result.roof);
                    updateHardware('designText', "Designing Your System...");
                    break;
                case 'design':
                    setDesignStatus('design');
                    updateHardware('designImage', result.design);
                    updateHardware('designText', "Finalizing System...");
                    break;
                case 'lidar':
                    setDesignStatus('lidar');
                    updateHardware('designImage', result.lidar);
                    updateHardware('designText', "Creating Irradiance Map...");
                    break;
                case 'irradiance':
                    setDesignStatus('irradiance');
                    updateHardware('designImage', result.irradiance);
                    updateHardware('designText', "Finalizing System...");
                    break;
                
                case 'completed':
                    setDesignStatus('completed');
                    updateHardware('systemSize', result.systemSize);
                    // energyOffset is a %-suffixed string from Aurora ("110%"),
                    // but guard it — completion no longer waits on every field.
                    if (result.energyOffset !== undefined && result.energyOffset !== null) {
                        updateHardware('offset', parseInt(String(result.energyOffset).replace('%', ''), 10));
                    }
                    // The design is reported complete as soon as Aurora finishes,
                    // which is BEFORE the screenshot bot captures the design view.
                    // Show the satellite image meanwhile, and swap in the real
                    // design shot on a later poll once it lands (the effect below
                    // keeps polling while screenshotsReady is false).
                    setScreenshotsReady(Boolean(result.screenshotsReady));
                    // Stop polling only when BOTH captures have landed, or the
                    // bot run has ended so neither is coming. Stopping at
                    // `screenshotsReady` (the overhead design) would leave the
                    // sun map frame shimmering: it lands seconds later, behind a
                    // 3D switch and an irradiance toggle.
                    setScreenshotsDone(Boolean(result.screenshotsComplete || result.screenshotsFinal));
                    updateHardware('designImage', result.screenshotCropped || hardware.satelliteImage);
                    setDesignInfo(result);
                    if (Array.isArray(result.monthlyProduction)) {
                        const sumOfProduction = result.monthlyProduction
                            .map(value => parseFloat(value))  // Convert each string to a decimal number
                            .reduce((acc, curr) => acc + curr, 0);
                        updateHardware('annualProduction', sumOfProduction);
                    } else if (result.annualProduction !== undefined) {
                        updateHardware('annualProduction', result.annualProduction);
                    }

                    // Call createProposals after setting the design status to 'completed'
                    // createProposals();
                    break;
                case 'incompatible':
                    setDesignStatus('incompatible');
                    updateHardware('designText', "We were not able to get up to date imaging for your home.");
                    break;
                case 'failed':
                    setDesignStatus('failed');
                    updateHardware('designText', "Skynet overload, retrying...");
                    break;
                case 'failedFinal':
                    setDesignStatus('failedFinal');
                    updateHardware('designText', "BEEP BOOP ERROR");
                    break;
                default:
                    setDesignStatus('loading');
                    updateHardware('designText', "Loading Home...");
            }

            // Applied AFTER the switch so it wins over the per-status wording
            // above ("Determining Roof Dimensions…", "Loading Home…"), which
            // predates real stage reporting and no longer matches what the
            // pipeline is actually doing. Keeps the bottom bar showing exactly
            // the stage the loading screen has highlighted — both read their
            // labels from constants/designStages. Terminal states keep their
            // own copy.
            const terminal = ['completed', 'failedFinal', 'incompatible', 'errored'];
            if (result.apiRunStatus && !terminal.includes(result.designStatus)) {
                updateHardware('designText', stageLabel(result.apiRunStatus));
            }
        } catch (error) {
        console.error('Error fetching design status:', error);
        }
    }, [formState.address, updateHardware, hardware.satelliteImage]);

    // Re-baseline the design inputs once the design settles.
    //
    // A completed design writes Aurora's real numbers back over the customer's
    // requested ones — most importantly `offset`, where asking for 103% and
    // getting 109% meant the snapshot taken at submit could never match again.
    // The button then read "Regenerate Design" forever, and because that check
    // takes precedence it also masked battery/EV changes. Re-snapshotting after
    // the writeback makes the baseline "the system as designed", so only the
    // customer's own later edits count as changes.
    //
    // Guarded by a ref: polling continues after completion (collecting
    // screenshots) and updates `hardware` each time, which would otherwise
    // re-baseline continuously and never detect anything.
    useEffect(() => {
        const settled = designStatus === 'completed' || designStatus === 'errored';
        if (settled && generatedInputs && !rebaselinedRef.current) {
            rebaselinedRef.current = true;
            setGeneratedInputs(designInputsOf(hardware));
            setGeneratedExtras(pricedExtrasOf(hardware));
        }
    }, [designStatus, hardware, generatedInputs]);

    // Function to reset design state
    const resetDesignState = useCallback(() => {
        setDesignStatus('pending');  
        setSubmitted(false);
        setDesignInfo(null);
        setIsPolling(false);
        updateHardware('designImage', hardware.satelliteImage);
        updateHardware('designText', "Loading Home...");
        updateHardware('designInfo', null);
        updateHardware('paymentType', '');
        setDesignLocked(false);
    }, [updateHardware, setDesignLocked, hardware.satelliteImage]);

    // Poll the design status every 5 seconds if design is not completed
    useEffect(() => {
        let intervalId;
        let timeoutId;

        // console.log("design status ", designStatus);

        const isTerminal = designStatus === 'completed' || designStatus === 'incompatible' || designStatus === 'failedFinal' || designStatus === 'errored';

        // The design now completes as soon as Aurora finishes, which is ~30-60s
        // BEFORE the screenshot bot captures the 3D view. Stopping at
        // 'completed' meant the page kept whatever image existed at that
        // instant — usually the satellite fallback — and never picked up the
        // design shot that arrived moments later. So keep polling through
        // completion until screenshots land, with a cap so a dead bot doesn't
        // poll forever.
        const awaitingScreenshots = designStatus === 'completed' && !screenshotsDone && screenshotWaits < MAX_SCREENSHOT_WAITS;

        if (submitted && isPolling && (!isTerminal || awaitingScreenshots)) {
            // Add a delay of 10 seconds before starting the interval
            timeoutId = setTimeout(() => {
                intervalId = setInterval(() => {
                    if (awaitingScreenshots) setScreenshotWaits((n) => n + 1);
                    fetchDesignStatus();
                }, awaitingScreenshots ? 3000 : 1000); // back off once the design itself is done
            }, 1000); // Delay the first fetch by 1 seconds
        }

        // Cleanup function: clear the timeout and interval
        return () => {
            clearTimeout(timeoutId);
            clearInterval(intervalId);
        };
    }, [designStatus, submitted, isPolling, fetchDesignStatus, screenshotsDone, screenshotWaits]);

    const stopPolling = () => {
        setIsPolling(false);
    };

    return {
        designStatus,
        setDesignStatus,
        designInfo,
        startAutomation,
        updateProposals,
        selectProposal,
        createDesign,
        stopPolling,
        resetDesignState,
        generatedInputs,
        generatedExtras
    };
};

export default useAutoDesigner;
